/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState } from "react";

import {
  AlertTriangle,
  ExternalLink,
  Monitor,
  Play,
  RefreshCw,
  Smartphone,
} from "lucide-react";

import {
  FRONTEND_BUILD_MESSAGES,
  getNextLoadingMessageIndex,
} from "./prototypeBuildMessages";

export type PreviewMode = "ready" | "generating" | "error";
type PreviewFrameLoadState = "idle" | "loading" | "loaded" | "slow" | "error";

interface PrototypePreviewPanelProps {
  mode: PreviewMode;
  previewUrl?: string | null;
  trackingUrl?: string | null;
  generationStatus?: string;
  generationError?: string | null;
  isInitialLoading?: boolean;
  showIdleLoader?: boolean;
  isPlanReady?: boolean;
  blockedMessage?: string | null;
  onRetry?: () => void;
}

export const PrototypePreviewPanel: React.FC<PrototypePreviewPanelProps> = ({
  mode,
  previewUrl,
  trackingUrl,
  generationStatus,
  generationError,
  isInitialLoading,
  showIdleLoader,
  isPlanReady,
  blockedMessage,
  onRetry,
}) => {
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeMessageIndex, setActiveMessageIndex] = useState(0);
  const [frameLoadState, setFrameLoadState] =
    useState<PreviewFrameLoadState>("idle");
  const [isFrameNoticeDismissed, setIsFrameNoticeDismissed] = useState(false);
  const hasGeneratedPreview = Boolean(previewUrl);
  const activeMessage = FRONTEND_BUILD_MESSAGES[activeMessageIndex];

  useEffect(() => {
    if (mode !== "generating") {
      setActiveMessageIndex(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveMessageIndex((current) =>
        getNextLoadingMessageIndex(FRONTEND_BUILD_MESSAGES.length, current),
      );
    }, 1900);

    return () => window.clearInterval(intervalId);
  }, [mode]);

  useEffect(() => {
    if (!hasGeneratedPreview || mode === "generating") {
      setFrameLoadState("idle");
      return;
    }

    setFrameLoadState("loading");
    setIsFrameNoticeDismissed(false);

    const timeoutId = window.setTimeout(() => {
      setFrameLoadState((current) =>
        current === "loading" ? "slow" : current,
      );
    }, 12000);

    return () => window.clearTimeout(timeoutId);
  }, [hasGeneratedPreview, mode, previewUrl, refreshKey]);

  const handleRefresh = () => {
    if (mode === "generating" || !hasGeneratedPreview) return;

    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
    window.setTimeout(() => setIsRefreshing(false), 800);
  };

  const handleOpenPreview = () => {
    if (!previewUrl) return;

    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const isPreviewFramePending =
    mode === "ready" &&
    hasGeneratedPreview &&
    (frameLoadState === "loading" || frameLoadState === "slow");
  const hasPreviewFrameError =
    mode === "ready" && hasGeneratedPreview && frameLoadState === "error";
  const shouldShowFrameNotice =
    mode === "ready" &&
    hasGeneratedPreview &&
    !isFrameNoticeDismissed &&
    (frameLoadState === "loading" ||
      frameLoadState === "slow" ||
      frameLoadState === "error");
  const normalizedGenerationStatus = generationStatus?.trim().toLowerCase();
  const statusConfig =
    mode === "generating"
      ? {
          label: generationStatus || "Generating",
          dotClassName:
            normalizedGenerationStatus === "queued"
              ? "bg-sky-400"
              : normalizedGenerationStatus === "submitting"
                ? "bg-violet-400"
                : normalizedGenerationStatus === "pending"
                  ? "bg-amber-300"
                  : "bg-amber-400",
        }
      : mode === "error"
        ? { label: "Error", dotClassName: "bg-red-500" }
      : hasPreviewFrameError
        ? { label: "Preview issue", dotClassName: "bg-red-400" }
      : isPreviewFramePending
        ? { label: "Loading preview", dotClassName: "bg-amber-300" }
      : hasGeneratedPreview
        ? { label: "Ready", dotClassName: "bg-green-500" }
        : blockedMessage
          ? { label: "Blocked", dotClassName: "bg-red-400" }
          : isPlanReady
            ? { label: "Plan Ready", dotClassName: "bg-green-400" }
          : showIdleLoader
            ? { label: "Preparing", dotClassName: "bg-amber-300" }
            : { label: "Awaiting plan", dotClassName: "bg-slate-500" };

  return (
    <div className="flex h-full w-full flex-col bg-black">
      <div className="flex h-12 w-full items-center justify-between border-b border-white/10 bg-[#0F0F0F] px-4">
        <div className="flex flex-1 items-center gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-2 w-2 rounded-full ${statusConfig.dotClassName}`}
            ></span>
            <span className="text-xs text-white/60">{statusConfig.label}</span>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end gap-2">
          <div className="mr-2 flex items-center rounded-lg bg-white/5 p-0.5">
            <button
              onClick={() => setViewport("desktop")}
              className={`flex h-7 w-8 items-center justify-center rounded-md transition-colors ${
                viewport === "desktop"
                  ? "bg-white/10 text-white"
                  : "text-white/40 hover:text-white/80"
              }`}
              title="Desktop View"
            >
              <Monitor size={14} />
            </button>
            <button
              onClick={() => setViewport("mobile")}
              className={`flex h-7 w-8 items-center justify-center rounded-md transition-colors ${
                viewport === "mobile"
                  ? "bg-white/10 text-white"
                  : "text-white/40 hover:text-white/80"
              }`}
              title="Mobile View"
            >
              <Smartphone size={14} />
            </button>
          </div>

          {trackingUrl ? (
            <button
              onClick={() =>
                window.open(trackingUrl, "_blank", "noopener,noreferrer")
              }
              className="flex h-8 items-center gap-1 rounded-lg px-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              title="Open tracking URL"
            >
              <ExternalLink size={14} />
              <span className="text-xs">Tracking</span>
            </button>
          ) : null}

          <div className="mx-1 h-4 w-px bg-white/10"></div>

          <button
            onClick={handleRefresh}
            disabled={mode === "generating" || !hasGeneratedPreview}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            title="Refresh Preview"
          >
            <RefreshCw
              size={14}
              className={isRefreshing ? "animate-spin" : ""}
            />
          </button>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            disabled={!hasGeneratedPreview}
            onClick={handleOpenPreview}
            title="Open preview in new tab"
          >
            <ExternalLink size={14} />
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden bg-black">
        <div
          className={`flex h-full w-full items-center justify-center ${
            viewport === "mobile" ? "p-4" : "p-0"
          }`}
        >
          <div
            className={`flex h-full flex-col overflow-hidden transition-all duration-300 ease-in-out ${
              viewport === "mobile"
                ? "w-[375px] rounded-[2.5rem] border-[8px] border-black bg-black ring-1 ring-white/20 shadow-2xl"
                : "w-full bg-transparent"
            }`}
          >
            <div
              className={`relative flex flex-1 items-center justify-center overflow-hidden ${
                viewport === "mobile" ? "rounded-[1.8rem]" : ""
              }`}
            >
              {mode === "generating" ? (
                <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.2),_transparent_32%),linear-gradient(180deg,_#0f1012_0%,_#080809_100%)] px-8 py-8">
                  <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_0%,rgba(251,191,36,0.05)_35%,transparent_70%)]"></div>
                  <div className="relative z-10 flex max-w-2xl flex-col items-center text-center">
                    <div className="relative flex h-24 w-24 items-center justify-center">
                      <div className="absolute inset-0 rounded-full border border-amber-300/20"></div>
                      <div className="absolute inset-2 rounded-full border border-amber-300/30 border-t-amber-200 animate-spin"></div>
                      <div className="absolute inset-5 rounded-full border border-transparent border-t-amber-400/80 animate-spin [animation-duration:1.6s]"></div>
                      <div className="h-3 w-3 rounded-full bg-amber-300 shadow-[0_0_30px_rgba(251,191,36,0.65)]"></div>
                    </div>

                    <div className="mt-8 text-[11px] font-semibold uppercase tracking-[0.28em] text-amber-300/80">
                      Frontend Build In Progress
                    </div>
                    <h3 className="mt-4 text-4xl font-semibold tracking-tight text-white">
                      Building your application preview
                    </h3>
                    <p className="mt-5 min-h-[64px] max-w-2xl text-lg leading-8 text-white/68 transition-opacity duration-300">
                      {activeMessage}
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-white/42">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300"></span>
                      The preview will appear automatically when the first
                      version is ready.
                    </div>
                  </div>
                </div>
              ) : hasGeneratedPreview ? (
                <>
                  <iframe
                    key={`${previewUrl}-${refreshKey}`}
                    src={previewUrl ?? undefined}
                    className="h-full w-full border-none bg-black outline-none"
                    title="Prototype Preview"
                    onLoad={() => setFrameLoadState("loaded")}
                    onError={() => setFrameLoadState("error")}
                  />

                  {shouldShowFrameNotice ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 p-6">
                      <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-white/10 bg-[#111111]/95 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.42)] backdrop-blur">
                        <div className="flex items-start gap-3">
                          <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-200">
                            {frameLoadState === "loading" ? (
                              <div className="absolute inset-2 rounded-full border border-amber-300/30 border-t-amber-200 animate-spin"></div>
                            ) : (
                              <AlertTriangle size={17} />
                            )}
                            <Play size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200/85">
                              {frameLoadState === "loading"
                                ? "Loading Preview"
                                : "Preview Still Loading"}
                            </div>
                            <div className="mt-2 text-[15px] font-medium text-white">
                              {frameLoadState === "loading"
                                ? "Starting the generated app"
                                : "The embedded preview is taking longer than expected"}
                            </div>
                            <div className="mt-2 text-[13px] leading-6 text-white/65">
                              {frameLoadState === "loading"
                                ? "v0 demo URLs can take a few moments to warm up after the URL is returned."
                                : "Chrome interrupted the embedded load. If the preview opens in a new tab, keep using that view or refresh here after the network settles."}
                            </div>
                            {frameLoadState !== "loading" ? (
                              <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                  onClick={handleRefresh}
                                  className="inline-flex items-center justify-center rounded-lg border border-white/12 bg-white/8 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-white/12"
                                >
                                  Refresh iframe
                                </button>
                                <button
                                  onClick={handleOpenPreview}
                                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-white px-3 py-2 text-xs font-medium text-black transition-colors hover:bg-white/90"
                                >
                                  <ExternalLink size={13} />
                                  Open preview
                                </button>
                                <button
                                  onClick={() => setIsFrameNoticeDismissed(true)}
                                  className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium text-white/70 transition-colors hover:bg-white/8 hover:text-white"
                                >
                                  Keep waiting
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {mode === "error" ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 p-6">
                      <div className="pointer-events-auto max-w-md rounded-3xl border border-red-500/20 bg-[#111111]/95 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.4)] backdrop-blur">
                        <div className="flex items-start gap-3">
                          <div className="rounded-2xl bg-red-500/10 p-3 text-red-300">
                            <AlertTriangle size={18} />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-white">
                              Prototype generation failed
                            </div>
                            <div className="mt-2 text-sm leading-6 text-white/65">
                              {generationError ||
                                "We could not finish building the prototype. You can retry the request from here."}
                            </div>
                          </div>
                        </div>

                        {onRetry ? (
                          <button
                            onClick={onRetry}
                            className="mt-5 inline-flex items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90"
                          >
                            Retry generation
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : mode === "error" ? (
                <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,_#0b0b0c_0%,_#080808_100%)] p-6">
                  <div className="max-w-md rounded-3xl border border-red-500/20 bg-[#111111]/95 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
                    <div className="flex items-start gap-3">
                      <div className="rounded-2xl bg-red-500/10 p-3 text-red-300">
                        <AlertTriangle size={18} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">
                          Prototype generation failed
                        </div>
                        <div className="mt-2 text-sm leading-6 text-white/65">
                          {generationError ||
                            "We could not finish building the prototype. Retry the request when you are ready."}
                        </div>
                      </div>
                    </div>

                    {onRetry ? (
                      <button
                        onClick={onRetry}
                        className="mt-5 inline-flex items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90"
                      >
                        Retry generation
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : blockedMessage ? (
                <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,_#0b0b0c_0%,_#080808_100%)] p-6">
                  <div className="w-full max-w-xl rounded-2xl border border-red-500/20 bg-[#111111]/95 p-6 shadow-[0_24px_60px_rgba(0,0,0,0.40)]">
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-red-500/10 p-3 text-red-300">
                        <AlertTriangle size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-200/90">
                          Preview Unavailable
                        </div>
                        <div className="mt-2 text-[15px] font-medium text-white">
                          The preview cannot be prepared yet
                        </div>
                        <div className="mt-2 text-[13px] leading-6 text-white/65">
                          {blockedMessage}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isPlanReady ? (
                <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.10),_transparent_30%),linear-gradient(180deg,_#0d0d0e_0%,_#090909_100%)] p-6">
                  <div className="w-full max-w-2xl rounded-[30px] border border-emerald-400/18 bg-[linear-gradient(135deg,rgba(16,185,129,0.08),rgba(255,255,255,0.02))] p-8 shadow-[0_24px_60px_rgba(0,0,0,0.42)]">
                    <div className="flex items-start gap-5">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-400/10 text-emerald-300">
                        <Play size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-200/85">
                          Ready To Generate
                        </div>
                        <h3 className="mt-3 text-[28px] font-semibold leading-9 tracking-tight text-white">
                          Your implementation plan is ready
                        </h3>
                        <p className="mt-3 max-w-xl text-[15px] leading-7 text-white/65">
                          Review the plan on the left, make any final refinements, and then click
                          <span className="mx-1 font-medium text-white">Implement Plan</span>
                          to start generating the prototype preview.
                        </p>
                        <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 px-4 py-4 text-left text-[13px] leading-6 text-white/58">
                          The generated experience will appear here once the build begins and the first version becomes available.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isInitialLoading || showIdleLoader || !hasGeneratedPreview ? (
                <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.10),_transparent_28%),linear-gradient(180deg,_#0d0d0e_0%,_#090909_100%)] p-6">
                  <div className="w-full max-w-xl rounded-2xl border border-amber-500/18 bg-gradient-to-br from-amber-200/10 to-white/5 p-5 shadow-[0_24px_60px_rgba(0,0,0,0.40)]">
                    <div className="flex items-start gap-4">
                      <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-amber-400/20 bg-amber-400/10 text-amber-300">
                        <div className="absolute inset-2 rounded-full border border-amber-300/30 border-t-amber-200 animate-spin"></div>
                        <Play size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-300/78">
                          Preview In Preparation
                        </div>
                        <div className="mt-2 text-[15px] font-medium text-white">
                          Preparing the preview workspace
                        </div>
                        <div className="mt-2 text-[13px] leading-6 text-white/60">
                          The preview area is getting ready. Once you start implementation, the generated experience will appear here automatically.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
